// CRUD admin de bonos_tipos.

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit2, Trash2, AlertCircle, Loader2, X } from 'lucide-react';
import { createPortal } from 'react-dom';

import { listBonosTipos, createBonoTipo, updateBonoTipo, deleteBonoTipo } from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

const FORM_VACIO = {
  nombre_es: '', nombre_en: '', nombre_ru: '', nombre_fr: '',
  descripcion_es: '', descripcion_en: '',
  descripcion_larga_es: '', descripcion_larga_en: '', descripcion_larga_ru: '', descripcion_larga_fr: '',
  tipo: 'bono', precio_euros: 0, validez_dias: 30, creditos_incluidos: 5,
  aplica_a: 'presencial', activo: true, orden_visualizacion: 0,
};

export default function AdminBonosTipos() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [aplicas, setAplicas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await listBonosTipos();
      setItems(r.data || []);
      setTipos(r.tipos_validos || []);
      setAplicas(r.aplica_validos || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const onBorrar = async (item) => {
    if (!window.confirm(t('admin.confirmar_desactivar', { nombre: item.nombre_es }))) return;
    try { await deleteBonoTipo(item.id); cargar(); }
    catch (e) { alert(e.message || t('admin.error_generico')); }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.bonos_tipos.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.bonos_tipos.sub')}</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setEditar(FORM_VACIO)}>
          <Plus size={16} strokeWidth={1.75} /> {t('admin.crear_nuevo')}
        </button>
      </header>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}

      {!loading && !error && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.bonos_tipos.col.nombre')}</th>
              <th>{t('admin.bonos_tipos.col.tipo')}</th>
              <th>{t('admin.bonos_tipos.col.precio')}</th>
              <th>{t('admin.bonos_tipos.col.creditos')}</th>
              <th>{t('admin.bonos_tipos.col.validez')}</th>
              <th>{t('admin.bonos_tipos.col.activo')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className={!b.activo ? 'inactivo' : ''}>
                <td>{b.nombre_es}</td>
                <td><span className="estado-pill">{b.tipo}</span></td>
                <td>{b.precio_euros} €</td>
                <td>{b.creditos_incluidos == null ? '∞' : b.creditos_incluidos}</td>
                <td>{b.validez_dias ? b.validez_dias + 'd' : '—'}</td>
                <td>{b.activo ? '✓' : '—'}</td>
                <td className="admin-acciones">
                  <button type="button" onClick={() => setEditar({ ...FORM_VACIO, ...b })} aria-label={t('admin.editar')}>
                    <Edit2 size={16} />
                  </button>
                  <button type="button" onClick={() => onBorrar(b)} aria-label={t('admin.borrar')}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editar && (
        <BonoModal item={editar} tipos={tipos} aplicas={aplicas}
                   onCerrar={() => setEditar(null)} onGuardado={() => { setEditar(null); cargar(); }} />
      )}
    </div>
  );
}

function BonoModal({ item, tipos, aplicas, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(item);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const editando = !!item.id;
  const esMembresia = String(form.tipo || '').startsWith('membresia');

  const setCampo = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setEnviando(true);
    try {
      const payload = {
        nombre_es: form.nombre_es, nombre_en: form.nombre_en,
        nombre_ru: form.nombre_ru || null, nombre_fr: form.nombre_fr || null,
        descripcion_es: form.descripcion_es || null, descripcion_en: form.descripcion_en || null,
        descripcion_larga_es: form.descripcion_larga_es || null,
        descripcion_larga_en: form.descripcion_larga_en || null,
        descripcion_larga_ru: form.descripcion_larga_ru || null,
        descripcion_larga_fr: form.descripcion_larga_fr || null,
        tipo: form.tipo,
        precio_euros: Number(form.precio_euros),
        validez_dias: form.validez_dias ? Number(form.validez_dias) : null,
        creditos_incluidos: esMembresia ? null : Number(form.creditos_incluidos),
        aplica_a: form.aplica_a,
        activo: !!form.activo,
        orden_visualizacion: Number(form.orden_visualizacion || 0),
      };
      if (editando) await updateBonoTipo(item.id, payload);
      else await createBonoTipo(payload);
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('admin.error_generico'));
    } finally { setEnviando(false); }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{editando ? t('admin.editar') : t('admin.crear_nuevo')}</h2>

        <form className="modal-form" onSubmit={submit}>
          <div className="admin-form-grid">
            <div className="modal-campo">
              <label>{t('admin.bonos_tipos.f.nombre_es')}</label>
              <input type="text" value={form.nombre_es} onChange={setCampo('nombre_es')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.bonos_tipos.f.nombre_en')}</label>
              <input type="text" value={form.nombre_en} onChange={setCampo('nombre_en')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.bonos_tipos.f.tipo')}</label>
              <select value={form.tipo} onChange={setCampo('tipo')}>
                {tipos.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.bonos_tipos.f.aplica_a')}</label>
              <select value={form.aplica_a} onChange={setCampo('aplica_a')}>
                {aplicas.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.bonos_tipos.f.precio')}</label>
              <input type="number" step="0.01" min="0" value={form.precio_euros} onChange={setCampo('precio_euros')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.bonos_tipos.f.validez')}</label>
              <input type="number" min="1" value={form.validez_dias || ''} onChange={setCampo('validez_dias')} />
            </div>
            {!esMembresia && (
              <div className="modal-campo">
                <label>{t('admin.bonos_tipos.f.creditos')}</label>
                <input type="number" min="1" value={form.creditos_incluidos || ''} onChange={setCampo('creditos_incluidos')} required />
              </div>
            )}
            <div className="modal-campo">
              <label>{t('admin.bonos_tipos.f.orden')}</label>
              <input type="number" value={form.orden_visualizacion || 0} onChange={setCampo('orden_visualizacion')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.bonos_tipos.f.descripcion_es')}</label>
              <textarea rows={2} value={form.descripcion_es || ''} onChange={setCampo('descripcion_es')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.bonos_tipos.f.descripcion_larga_es')}</label>
              <textarea rows={5} value={form.descripcion_larga_es || ''} onChange={setCampo('descripcion_larga_es')}
                placeholder={t('admin.bonos_tipos.f.placeholder')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.bonos_tipos.f.descripcion_larga_en')}</label>
              <textarea rows={5} value={form.descripcion_larga_en || ''} onChange={setCampo('descripcion_larga_en')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.bonos_tipos.f.descripcion_larga_ru')}</label>
              <textarea rows={5} value={form.descripcion_larga_ru || ''} onChange={setCampo('descripcion_larga_ru')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.bonos_tipos.f.descripcion_larga_fr')}</label>
              <textarea rows={5} value={form.descripcion_larga_fr || ''} onChange={setCampo('descripcion_larga_fr')} />
            </div>
            <div className="modal-campo full">
              <label>
                <input type="checkbox" checked={!!form.activo} onChange={setCampo('activo')} />
                {' '}{t('admin.bonos_tipos.f.activo')}
              </label>
            </div>
          </div>

          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}

          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
